import Dexie, { type Table } from 'dexie';

export interface LocalPhoto {
  id: string;
  calibrationId: string;
  filename: string;
  blob: Blob;
  thumbnailDataUrl: string;
  mimeType: string;
  size: number;
  capturedAt: Date;
  createdAt: Date;
  isCoverPhoto: boolean;
  isPromotedToWorkOrder: boolean;
  caption: string;
}

export class PhotosDatabase extends Dexie {
  photos!: Table<LocalPhoto>;

  constructor() {
    super('CalibrationPhotos');

    this.version(3).stores({
      photos: 'id, calibrationId, filename, createdAt',
      photoBlobs: null,
      uploadQueue: null,
      photoMetadataCache: null,
      storageQuotas: null
    });
  }

  async getPhoto(photoId: string): Promise<LocalPhoto | undefined> {
    return this.photos.get(photoId);
  }

  async savePhoto(photo: LocalPhoto): Promise<string> {
    await this.photos.put(photo);
    return photo.id;
  }

  async updatePhoto(
    photoId: string,
    updates: Partial<
      Pick<LocalPhoto, 'isCoverPhoto' | 'isPromotedToWorkOrder' | 'caption'>
    >
  ): Promise<void> {
    await this.photos.update(photoId, updates);
  }

  async deletePhoto(photoId: string): Promise<void> {
    await this.photos.delete(photoId);
  }

  async getPhotosForCalibration(calibrationId: string): Promise<LocalPhoto[]> {
    return this.photos
      .where('calibrationId')
      .equals(calibrationId)
      .sortBy('createdAt');
  }

  async deletePhotosForCalibration(calibrationId: string): Promise<void> {
    await this.photos.where('calibrationId').equals(calibrationId).delete();
  }

  async setCoverPhoto(calibrationId: string, photoId: string): Promise<void> {
    const photos = await this.getPhotosForCalibration(calibrationId);
    await this.transaction('rw', this.photos, async () => {
      for (const photo of photos) {
        await this.photos.update(photo.id, {
          isCoverPhoto: photo.id === photoId
        });
      }
    });
  }

  async getStorageUsage(calibrationId: string): Promise<number> {
    const photos = await this.getPhotosForCalibration(calibrationId);
    return photos.reduce((total, photo) => total + (photo.blob?.size || 0), 0);
  }

  async getTotalStorageUsed(): Promise<number> {
    const allPhotos = await this.photos.toArray();
    return allPhotos.reduce(
      (total, photo) => total + (photo.blob?.size || 0),
      0
    );
  }

  async cleanupOldPhotos(maxAgeInDays: number): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - maxAgeInDays);

    const oldPhotos = await this.photos
      .where('createdAt')
      .below(cutoffDate)
      .toArray();

    for (const photo of oldPhotos) {
      await this.photos.delete(photo.id);
    }

    return oldPhotos.length;
  }

  async clearAllData(): Promise<void> {
    await this.photos.clear();
  }
}

export const photosDb = new PhotosDatabase();
